import { normalizeIssuedAssignmentEntries } from "@/services/assignment";
import type { UserData } from "@/types/user-type";
import type {
  IssuedSnapshotAssignmentEntry,
  IssuedSnapshotAssignmentsRoot,
} from "@vTypes/snapshot-type";

/** Radix Select `value`는 빈 문자열 비권장 — 빈 id 는 이 토큰으로 표현 */
export const ISSUED_ASSIGNMENT_NO_GROUP_VALUE = "__no_group__";

export type IssuedAssignmentWardOption = {
  /** Select `value` */
  value: string;
  label: string;
  /** API `target_group_id` 원본(빈 문자열 가능) */
  rawGroupId: string;
};

export function listMyIssuedAssignmentEntries(
  root: IssuedSnapshotAssignmentsRoot | null | undefined,
  nurseId: string | null | undefined
): IssuedSnapshotAssignmentEntry[] {
  if (!root || nurseId == null || nurseId === "") return [];
  const list = root[String(nurseId)];
  return normalizeIssuedAssignmentEntries(Array.isArray(list) ? list : []);
}

export function wardOptionsFromIssuedAssignmentEntries(
  entries: IssuedSnapshotAssignmentEntry[]
): IssuedAssignmentWardOption[] {
  const byRawId = new Map<string, string>();
  for (const e of entries) {
    const raw = e.target_group_id ?? "";
    const label = (e.target_group_name?.trim() || raw || "미지정").trim();
    if (!byRawId.has(raw)) byRawId.set(raw, label);
  }
  return [...byRawId.entries()].map(([rawGroupId, label]) => ({
    rawGroupId,
    value:
      rawGroupId !== "" ? rawGroupId : ISSUED_ASSIGNMENT_NO_GROUP_VALUE,
    label,
  }));
}

export function rawGroupIdFromSelectValue(value: string): string {
  return value === ISSUED_ASSIGNMENT_NO_GROUP_VALUE ? "" : value;
}

/** 내 근무표 병동 뱃지·캘린더 라벨 바 — `mergeWardOptionsWithUserHome` 결과 순서 기준 인덱스 팔레트 */
const WARD_BADGE_PALETTE = [
  "#111827",
  "#0e59c6",
  "#10b981",
  "#f59e0b",
  "#8b5cf6",
  "#ef4444",
  "#14b8a6",
  "#64748b",
];

export function assignIndexPalette(
  options: IssuedAssignmentWardOption[]
): Map<string, string> {
  const m = new Map<string, string>();
  options.forEach((o, i) => {
    m.set(o.rawGroupId, WARD_BADGE_PALETTE[i % WARD_BADGE_PALETTE.length]);
  });
  return m;
}

function userHomeWardLabel(user: UserData): string {
  const id = (user.group_id ?? "").trim();
  if (id === "") return "";
  return (
    (user.mb_part_name ?? "").trim() ||
    (user.office_name ?? "").trim() ||
    id ||
    "미지정"
  );
}

/**
 * assignments로 만든 병동 옵션 + 세션 사용자 소속 병동(`UserData`) 병합.
 * - API에 현재 소속 `group_id`가 없으면 옵션 맨 앞에 유저 소속 1건 추가
 * - 같은 `group_id`가 있으면 **표시명은 항상 유저 데이터 우선** (API `target_group_name`으로 덮이지 않음)
 */
export function mergeWardOptionsWithUserHome(
  assignmentOptions: IssuedAssignmentWardOption[],
  user: UserData | null | undefined
): IssuedAssignmentWardOption[] {
  if (user == null) return assignmentOptions;

  const homeId = (user.group_id ?? "").trim();
  const homeLabel = userHomeWardLabel(user);
  if (homeId === "" || homeLabel === "") return assignmentOptions;

  const withUserLabels = assignmentOptions.map((o) =>
    o.rawGroupId === homeId ? { ...o, label: homeLabel } : o
  );

  const hasHome = withUserLabels.some((o) => o.rawGroupId === homeId);
  if (hasHome) return withUserLabels;

  return [
    {
      rawGroupId: homeId,
      value: homeId,
      label: homeLabel,
    },
    ...withUserLabels,
  ];
}
